import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { deleteServiceProviders, exportToExcel, sendEmail } from "../../api";
import { DeleteRecord, EditRecord, Pagination } from "../core";
import { ConfirmationModal } from "../modals/ConfirmationModal";
import DetailsModal from "../modals/DetailsModal";
import { setNotification } from "../../redux/notificationSlice";

export const SearchVendorResult = ({ searchResult }) => {
  const [verifiedData, setVerifiedData] = useState([]);
  const [deleteSPModal, setDeleteSPModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [details, setDetails] = useState(null);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const totalPages = Math.ceil(verifiedData.length / itemsPerPage);
  const currentItems = verifiedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const userInfo = useSelector((state) => state?.user?.profile);
  useEffect(() => {
    setVerifiedData(searchResult || []);
  }, [searchResult]);
  const onCloseDetails = () => {
    setShowModal(false);
  };
  
  const openUrl = (url) => {
    const normalizedUrl = url.startsWith('http://') || url.startsWith('https://')
      ? url
      : `https://${url}`;
    window.open(normalizedUrl, '_blank', 'noopener,noreferrer');
  };

  const openSPDeleteModal = (data) => {
    setDeleteSPModal(true);
    setRowToDelete(data);
  };
  const deleteSPRow = async () => {
    const dataToDelete = {
      operation: "removeServiceProvider",
      request: { id: rowToDelete.id, username: userInfo.isid },
    };
    const deleteResp = await deleteServiceProviders(dataToDelete);
    if (deleteResp.responseCode === 200) {
      setVerifiedData(
        verifiedData.filter((item) => item.id !== rowToDelete.id)
      );
      setDeleteSPModal(false);
      const emailBody = `<div className="bg-gray-100 p-8 font-sans">
        <div className="max-w-xl mx-auto bg-white rounded-lg shadow-md p-8">
          <p className="mb-4">Dear ${rowToDelete.primaryName},</p>
          <p className="mb-2">
            The service provider details with Request ID # ${rowToDelete.requestId} is deleted from the system.
          </p>
          <p className="mb-4">Please do not reply to this email as this is an automated email.</p>
          <p className="mb-4">
            Thanks!</br>
            MSD GCTO Service Portal
          </p>
        </div>
      </div>`;
      const deleteEmailSubject =
        "[MSD GCTO Service Provider Portal] Service Provider Data Deleted";
      sendEmail(deleteEmailSubject, emailBody, [rowToDelete.primaryEmail]);
      setRowToDelete(null);
      dispatch(
        setNotification({
          type: "success",
          show: true,
          message: "Success! service provider deleted successfully",
        })
      );
    }
  };

  const updateServiceProvider = (data) => {
    navigate("/update-serviceprovider", { state: data });
  };

  const showDetailsModal = (data) => {
    setShowModal(true);
    setDetails(data);
  };

  return (
    <>
      {Array.isArray(verifiedData) && verifiedData.length > 0 && (
        <>
          <div className="text-sm font-medium text-gray-500 border-b border-gray-200 dark:text-gray-400 dark:border-gray-700">
            <div className="relative flex py-1 items-center">
                <div className="flex-grow border-t border-gray-400"></div>
            </div>
            <ul className="flex flex-wrap -mb-px justify-between w-full">
              <li className="me-2">
                <h1 className="text-lg font-medium pt-2">Search Result</h1>
              </li>
              <li className="ml-4">
                <button
                  onClick={() => exportToExcel(verifiedData)}
                  className="bg-teal-100 text-black text-center text-base py-2 px-4 font-normal my-1 hover:text-white hover:bg-teal-500"
                >
                  Export Selected Service Provider Data
                </button>
              </li>
            </ul>
          </div>
          <div className="w-full max-h-[30rem] mb-10">
            <table className="table-fixed border border-slate-400 w-full">
              <thead className="bg-gray-300 sticky top-0 z-10">
                <tr className="border border-slate-300">
                  <th className="px-2 py-2 w-[66px]">Sr No.</th>
                  <th className="px-1 py-2">Service Provider Name</th>
                  <th className="px-1 py-2">Service Provider Url</th>
                  <th className="px-1 py-2">Request ID</th>
                  <th className="px-1 py-2">Primary Name</th>
                  <th className="px-1 py-2">Primary Email</th>
                  {userInfo && userInfo.role !== "Read" ? (
                    <th className="px-1 py-2"></th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {currentItems?.map((row, index) => (
                  <tr key={row.id} className="border border-slate-300 break-all max-w-xs">
                    <td className="px-2 py-1">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    <td className="px-1 py-1">
                      <button
                        onClick={() => showDetailsModal(row)}
                        className="text-left text-blue-600 hover:text-blue-800 visited:text-purple-600"
                      >
                        {row.serviceProviderName}
                      </button>
                    </td>
                    <td className="px-1 py-1">
                      <button
                        onClick={() => openUrl(row.serviceProviderURL)}
                        className="text-left text-blue-600 hover:text-blue-800 visited:text-purple-600"
                      >
                        {row.serviceProviderURL}
                      </button>
                    </td>
                    <td className="px-1 py-1">{row.requestId}</td>
                    <td className="px-1 py-1">{row.primaryName}</td>
                    <td className="px-1 py-1">{row.primaryEmail}</td>
                    {userInfo && userInfo.role !== "Read" ? (
                      <td className="px-1 py-1 text-right">
                        <button
                          className="text-white px-2 py-px"
                          value={row.id}
                          data-testid="editsp"
                          onClick={() => updateServiceProvider(row)}
                        >
                          <EditRecord />
                        </button>
                        <button
                          className="text-white px-2 py-px ml-2"
                          value={row.id}
                          data-testid="deletesp"
                          onClick={() => openSPDeleteModal(row)}
                        >
                          <DeleteRecord />
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
            {verifiedData.length > itemsPerPage && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </div>
          <DetailsModal
            showModal={showModal}
            onClose={() => onCloseDetails()}
            details={details}
          />
          <ConfirmationModal
            isOpen={deleteSPModal}
            onClose={() => setDeleteSPModal(false)}
            onDelete={() => deleteSPRow()}
            header="Confirm delete Service Provider"
            message="Are you sure you want to delete? This action cannot be undone."
          />
        </>
      )}
    </>
  );
};

SearchVendorResult.propTypes = {
  searchResult: PropTypes.object,
};
